import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { api, ApiError } from '../lib/api';
import { dbg, dbgErr } from '../lib/debug';
import ErrorToastHost from './ErrorToastHost';
import InstallPrompt from './InstallPrompt';

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      dbg('page', 'Layout: no auth user — clearing profile');
      setMe(null);
      return;
    }
    dbg('page', `Layout: fetching /api/users/me for uid ${user.uid}`);
    (async () => {
      try {
        const res = await api.get('/api/users/me');
        if (!cancelled) {
          dbg('page', 'Layout: profile loaded', {
            credits: res.credits,
            is_admin: res.is_admin,
            dissertations_generated: res.dissertations_generated,
          });
          setMe(res);
        }
      } catch (e) {
        dbgErr('err', 'Layout: /api/users/me failed', e);
        if (!cancelled && !(e instanceof ApiError && e.status === 404)) {
          setMe(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, location.pathname]);

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const credits = me?.credits ?? 0;
  const creditClass =
    credits <= 0 ? 'credit-badge is-empty' : credits >= 31 ? 'credit-badge is-good' : 'credit-badge';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <ErrorToastHost />
      <InstallPrompt />
      <nav className="navbar navbar-expand-lg navbar-main">
        <div className="container">
          <Link className="navbar-brand" to="/">
            <span className="brand-mark">
              <i className="fas fa-graduation-cap"></i>
            </span>
            <span>
              <span className="brand-word">Dissertation</span>
              <span className="brand-word" style={{ color: 'var(--primary)' }}> Island</span>
            </span>
          </Link>
          <button
            className="navbar-toggler border-0 p-2"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#mainNav"
            style={{ color: 'var(--ink)' }}
          >
            <i className="fas fa-bars"></i>
          </button>
          <div className="collapse navbar-collapse" id="mainNav">
            <ul className="navbar-nav mx-lg-auto">
              {!user ? (
                <>
                  <li className="nav-item">
                    <NavLink
                      className={`nav-link ${isActive('/') && location.pathname === '/' ? 'active' : ''}`}
                      to="/"
                    >
                      Home
                    </NavLink>
                  </li>
                  <li className="nav-item">
                    <NavLink className={`nav-link ${isActive('/pricing') ? 'active' : ''}`} to="/pricing">
                      Pricing
                    </NavLink>
                  </li>
                  <li className="nav-item">
                    <NavLink className={`nav-link ${isActive('/samples') ? 'active' : ''}`} to="/samples">
                      Samples
                    </NavLink>
                  </li>
                  <li className="nav-item">
                    <NavLink className={`nav-link ${isActive('/developers') ? 'active' : ''}`} to="/developers">
                      Developer account
                    </NavLink>
                  </li>
                  <li className="nav-item">
                    <NavLink className={`nav-link ${isActive('/help') ? 'active' : ''}`} to="/help">
                      Help
                    </NavLink>
                  </li>
                </>
              ) : (
                <>
                  <li className="nav-item">
                    <NavLink className={`nav-link ${isActive('/dashboard') ? 'active' : ''}`} to="/dashboard">
                      Dashboard
                    </NavLink>
                  </li>
                  <li className="nav-item">
                    <NavLink className={`nav-link ${isActive('/generate') ? 'active' : ''}`} to="/generate">
                      Dissertations
                    </NavLink>
                  </li>
                  <li className="nav-item">
                    <NavLink className={`nav-link ${isActive('/assignments') ? 'active' : ''}`} to="/assignments">
                      Assignments
                    </NavLink>
                  </li>
                  <li className="nav-item">
                    <NavLink className={`nav-link ${isActive('/worldbank') ? 'active' : ''}`} to="/worldbank">
                      Time Series
                    </NavLink>
                  </li>
                  <li className="nav-item">
                    <NavLink className={`nav-link ${isActive('/pricing') ? 'active' : ''}`} to="/pricing">
                      Pricing
                    </NavLink>
                  </li>
                  <li className="nav-item">
                    <NavLink className={`nav-link ${isActive('/help') ? 'active' : ''}`} to="/help">
                      Help
                    </NavLink>
                  </li>
                  {me?.is_admin && (
                    <li className="nav-item">
                      <NavLink className={`nav-link ${isActive('/admin') ? 'active' : ''}`} to="/admin">
                        Admin
                      </NavLink>
                    </li>
                  )}
                </>
              )}
            </ul>
            <ul className="navbar-nav align-items-lg-center">
              {user ? (
                <>
                  <li className="nav-item me-lg-3 mb-2 mb-lg-0">
                    <Link to="/pricing" className="text-decoration-none">
                      <span className={creditClass}>
                        <i className="fas fa-coins"></i>
                        {credits.toLocaleString()}
                      </span>
                    </Link>
                  </li>
                  <li className="nav-item dropdown">
                    <a
                      className="nav-link dropdown-toggle d-flex align-items-center gap-2"
                      href="#"
                      role="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <span
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: 8,
                          background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                          color: '#fff',
                          display: 'inline-grid',
                          placeItems: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                        }}
                      >
                        {(me?.full_name || user.email || 'U').charAt(0).toUpperCase()}
                      </span>
                      <span className="d-none d-lg-inline">
                        {me?.full_name?.split(' ')[0] || user.displayName || 'Account'}
                      </span>
                    </a>
                    <ul className="dropdown-menu dropdown-menu-end">
                      <li>
                        <Link className="dropdown-item" to="/dashboard">
                          <i className="fas fa-th-large me-2 text-muted"></i>Dashboard
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" to="/pricing">
                          <i className="fas fa-coins me-2 text-muted"></i>Buy credits
                        </Link>
                      </li>
                      {me?.is_admin && (
                        <>
                          <li>
                            <hr className="dropdown-divider" />
                          </li>
                          <li>
                            <Link className="dropdown-item" to="/admin">
                              <i className="fas fa-shield-alt me-2 text-muted"></i>Admin
                            </Link>
                          </li>
                          <li>
                            <Link className="dropdown-item" to="/admin/test">
                              <i className="fas fa-flask me-2 text-muted"></i>Prompt testing
                            </Link>
                          </li>
                        </>
                      )}
                      <li>
                        <hr className="dropdown-divider" />
                      </li>
                      <li>
                        <button
                          className="dropdown-item text-danger"
                          type="button"
                          onClick={handleLogout}
                        >
                          <i className="fas fa-sign-out-alt me-2"></i>Log out
                        </button>
                      </li>
                    </ul>
                  </li>
                </>
              ) : (
                <>
                  <li className="nav-item me-lg-2">
                    <NavLink className="nav-link" to="/login">
                      Log in
                    </NavLink>
                  </li>
                  <li className="nav-item">
                    <Link className="btn btn-ink btn-sm" to="/register" style={{ padding: '8px 18px' }}>
                      Get started
                      <i className="fas fa-arrow-right ms-2" style={{ fontSize: '0.75rem' }}></i>
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
      </nav>

      <main style={{ flex: 1 }}>{children}</main>

      <footer className="site-footer">
        <div className="container">
          <div className="row gy-4 align-items-start">
            <div className="col-md-5">
              <div className="d-flex align-items-center gap-2 mb-2">
                <span
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                    color: '#fff',
                    display: 'inline-grid',
                    placeItems: 'center',
                    fontSize: '0.9rem',
                  }}
                >
                  <i className="fas fa-graduation-cap"></i>
                </span>
                <span className="brand-word">Dissertation Island</span>
              </div>
              <p className="small" style={{ maxWidth: 320, color: 'rgba(255,255,255,0.55)', margin: 0 }}>
                Academic writing — full 5-chapter dissertations, assignments, and data analysis.
              </p>
            </div>
            <div className="col-md-3">
              <div
                className="fw-semibold mb-2"
                style={{ color: '#fff', fontSize: '0.85rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}
              >
                Product
              </div>
              <ul className="list-unstyled small" style={{ lineHeight: 2 }}>
                <li><Link to="/pricing">Pricing</Link></li>
                <li><Link to="/samples">Samples</Link></li>
                <li><Link to="/generate">Generate</Link></li>
                <li><Link to="/worldbank">World Bank analysis</Link></li>
              </ul>
            </div>
            <div className="col-md-2">
              <div
                className="fw-semibold mb-2"
                style={{ color: '#fff', fontSize: '0.85rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}
              >
                Account
              </div>
              <ul className="list-unstyled small" style={{ lineHeight: 2 }}>
                <li><Link to="/login">Log in</Link></li>
                <li><Link to="/register">Create account</Link></li>
                <li><Link to="/dashboard">Dashboard</Link></li>
                <li><Link to="/developers">Developer account</Link></li>
              </ul>
            </div>
            <div className="col-md-2">
              <div
                className="fw-semibold mb-2"
                style={{ color: '#fff', fontSize: '0.85rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}
              >
                Get in touch
              </div>
              <ul className="list-unstyled small" style={{ lineHeight: 2 }}>
                <li>
                  <a href="mailto:silas.phiri21@gmail.com">
                    <i className="fas fa-envelope me-1"></i>
                    silas.phiri21@gmail.com
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <hr style={{ borderColor: 'rgba(255,255,255,0.08)', margin: '2rem 0 1.2rem' }} />
          <div className="d-flex justify-content-between flex-wrap gap-2">
            <small>&copy; {new Date().getFullYear()} Dissertation Island. All rights reserved.</small>
            <small>
              <Link to="/terms" style={{ color: 'rgba(255,255,255,0.55)' }}>
                Terms &amp; Conditions
              </Link>
              <span className="mx-2" style={{ color: 'rgba(255,255,255,0.25)' }}>·</span>
              Made with care for researchers &amp; students.
            </small>
          </div>
        </div>
      </footer>
    </div>
  );
}
